"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObservableEventEmitter = void 0;
const events_typed_1 = require("@video/events-typed");
const mobx_1 = require("mobx");
const adapter_1 = require("../../../api/adapter");
const errors_deprecated_1 = require("../../errors-deprecated");
const bind_1 = require("../bind");
const context_1 = require("../context/context");
const vc_context_1 = require("../context/vc-context");
const dispose_1 = require("../dispose");
function isReactive(obj, prop) {
    return (0, mobx_1.isObservableProp)(obj, prop) || (0, mobx_1.isComputedProp)(obj, prop);
}
function getter(obj, prop) {
    return () => obj[prop];
}
const Emitter = Symbol("Emitter");
const Observers = Symbol("Observers");
// eslint-disable-next-line @typescript-eslint/ban-types
class ObservableEventEmitter {
    constructor(bounded = true) {
        this[_a] = new events_typed_1.EventEmitter();
        this[_b] = new Map();
        this.disposers = [];
        this.disposed = false;
        this.disposing = false;
        if (bounded) {
            (0, bind_1.makeBounded)(this, [], false);
        }
    }
    startEmitting(prop) {
        if (this[Observers].has(prop)) {
            return;
        }
        const handler = this[Emitter].emit.bind(this[Emitter], prop);
        const disposer = (0, mobx_1.reaction)(getter(this, prop), handler, { name: `event:${this.constructor.name}.${String(prop)}` });
        this[Observers].set(prop, disposer);
    }
    stopEmitting(prop) {
        var _c;
        (_c = this[Observers].get(prop)) === null || _c === void 0 ? void 0 : _c();
        this[Observers].delete(prop);
    }
    setMaxListeners(n) {
        this[Emitter].setMaxListeners(n);
        return this;
    }
    getMaxListeners() {
        return this[Emitter].getMaxListeners();
    }
    addListener(type, listener) {
        this[Emitter].addListener(type, listener);
        if (isReactive(this, type)) {
            this.startEmitting(type);
        }
        return this;
    }
    emit(type, ev, ...args) {
        return this[Emitter].emit(type, ev, ...args);
    }
    off(type, listener) {
        this[Emitter].off(type, listener);
        if (events_typed_1.EventEmitter.listenerCount(this[Emitter], type) === 0) {
            this.stopEmitting(type);
        }
        return this;
    }
    on(type, listener) {
        this[Emitter].on(type, listener);
        if (isReactive(this, type)) {
            this.startEmitting(type);
        }
        return this;
    }
    once(type, listener) {
        this[Emitter].once(type, listener);
        if (isReactive(this, type)) {
            this.startEmitting(type);
        }
        return this;
    }
    prependListener(type, listener) {
        this[Emitter].prependListener(type, listener);
        if (isReactive(this, type)) {
            this.startEmitting(type);
        }
        return this;
    }
    prependOnceListener(type, listener) {
        this[Emitter].prependOnceListener(type, listener);
        if (isReactive(this, type)) {
            this.startEmitting(type);
        }
        return this;
    }
    removeAllListeners(type) {
        this[Emitter].removeAllListeners(type);
        for (const key of this[Observers].keys()) {
            this.stopEmitting(key);
        }
        return this;
    }
    removeListener(type, listener) {
        this[Emitter].removeListener(type, listener);
        if (events_typed_1.EventEmitter.listenerCount(this[Emitter], type) === 0) {
            this.stopEmitting(type);
        }
        return this;
    }
    addInnerDisposer(...disposableObjects) {
        for (const obj of disposableObjects) {
            if (typeof obj === "function") {
                this.disposers.unshift(obj);
            }
            else {
                this.disposers.unshift(obj.dispose.bind(obj));
            }
        }
    }
    autorun(view) {
        this.addInnerDisposer((0, mobx_1.autorun)(view));
    }
    get isDisposed() {
        return this.disposed;
    }
    emitError(err) {
        this[Emitter].emit("error", err);
        if (!err.isMuted && (0, vc_context_1.hasVcContext)(this)) {
            errors_deprecated_1.VideoClientErrorDeprecated.log(err, this.ctx.logger);
        }
    }
    throwError(err) {
        this.emitError(err);
        throw err;
    }
    throwErrorDeprecated(err) {
        this.emitErrorDeprecated(err);
        throw err;
    }
    emitErrorDeprecated(err) {
        try {
            this[Emitter].emit("error", err);
            if (err.critical) {
                this.dispose(`due error: ${err.code}`);
            }
        }
        catch (handlerErr) {
            if ((0, vc_context_1.hasVcContext)(this)) {
                this.ctx.logger.error("error handler throws another error", { err, handlerErr: (0, errors_deprecated_1.wrapNativeError)(handlerErr) });
            }
            else if (adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING)) {
                adapter_1.device.console.error("error handler throws another error", { err, handlerErr });
            }
        }
    }
    dispose(reason = "not provided") {
        if (this.disposed || this.disposing) {
            return;
        }
        this.disposing = true;
        (0, dispose_1.disposeObjects)(this, this.disposers, reason);
        this.removeAllListeners();
        if ((0, vc_context_1.hasVcContext)(this)) {
            // at this point the context should be canceled
            // but still call it explicitly to make sure
            // the context clear all refs to another objects
            (0, context_1.cancel)(this.ctx);
        }
        this.disposed = true;
        this.emit("disposed");
    }
}
exports.ObservableEventEmitter = ObservableEventEmitter;
_a = Emitter, _b = Observers;
//# sourceMappingURL=event-emitter.js.map